/**
 * Newsletter — view subscribers + compose and send a broadcast.
 *
 * Data feeds:
 *   - GET  /admin/newsletter        — counts + the subscriber list
 *   - POST /admin/newsletter/send   — send (test or to all active)
 *
 * The compose body is plain text with a tiny safe subset: blank lines
 * make paragraphs, **bold**, and [text](https://url) links. The backend
 * escapes everything else and appends a one-click unsubscribe footer.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, Copy, Eye, Loader2, Mail, Send } from "lucide-react";

import {
  getNewsletter,
  previewNewsletter,
  sendNewsletter,
  type NewsletterResponse,
  type NewsletterSendResult,
} from "@/api/admin";
import { cn, fmtRelative } from "@/lib/utils";

export function NewsletterPage() {
  const [data, setData] = useState<NewsletterResponse | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState<"test" | "all" | null>(null);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewing, setPreviewing] = useState(false);

  // Live preview — debounced so we render through the SAME branded
  // template the send uses (byte-for-byte what recipients get).
  useEffect(() => {
    if (!subject.trim() && !body.trim()) {
      setPreviewHtml("");
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const t = setTimeout(() => {
      previewNewsletter({ subject, body })
        .then((r) => {
          if (!cancelled) setPreviewHtml(r.html);
        })
        .catch(() => {
          /* preview is best-effort — leave the last good render */
        })
        .finally(() => {
          if (!cancelled) setPreviewing(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [subject, body]);

  async function load() {
    try {
      setData(await getNewsletter());
    } catch (e: unknown) {
      setLoadErr(
        (e as { message?: string })?.message || "Failed to load subscribers",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);

  const active = data?.total_active ?? 0;
  const canSend = subject.trim().length > 0 && body.trim().length > 0;

  async function doSend(mode: "test" | "all") {
    if (!canSend || sending) return;
    if (mode === "test" && !testEmail.trim()) {
      setResult({ ok: false, msg: "Enter a test email address first." });
      return;
    }
    if (
      mode === "all" &&
      !window.confirm(
        `Send this newsletter to all ${active} active subscriber${active === 1 ? "" : "s"}? This cannot be undone.`,
      )
    ) {
      return;
    }
    setSending(mode);
    setResult(null);
    try {
      const r: NewsletterSendResult = await sendNewsletter({
        subject: subject.trim(),
        body,
        ...(mode === "test" ? { test_email: testEmail.trim() } : {}),
      });
      const where = r.test ? `test → ${testEmail.trim()}` : "all subscribers";
      setResult({
        ok: r.failed === 0,
        msg: `Sent ${r.sent}/${r.recipients} (${where})${r.failed ? `, ${r.failed} failed` : ""}.`,
      });
    } catch (e: unknown) {
      setResult({
        ok: false,
        msg:
          (e as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail || "Send failed.",
      });
    } finally {
      setSending(null);
    }
  }

  const activeEmails = useMemo(
    () =>
      (data?.subscribers ?? [])
        .filter((s) => s.is_active)
        .map((s) => s.email),
    [data],
  );

  async function copyEmails() {
    try {
      await navigator.clipboard.writeText(activeEmails.join(", "));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  }

  return (
    <div className="p-6 space-y-6">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Newsletter</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          {data
            ? `${data.total_active} active · ${data.total_lifetime} lifetime`
            : "—"}{" "}
          subscribers.
        </p>
      </header>

      {/* ── Compose + live preview ──────────────────────────── */}
      <section className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-900 font-medium">
          <Mail className="h-4 w-4 text-accent" /> Compose broadcast
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left — editor */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Subject
              </label>
              <input
                type="text"
                value={subject}
                maxLength={200}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="What's new at Lenavio"
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">
                Body
              </label>
              <textarea
                value={body}
                maxLength={50000}
                onChange={(e) => setBody(e.target.value)}
                rows={14}
                placeholder={
                  "Hi there,\n\nBlank lines make new paragraphs. You can use **bold** and [links](https://lenavio.com).\n\n— Angel"
                }
                className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
              />
              <p className="text-[11px] text-slate-400 mt-1">
                Plain text. Blank line = paragraph · <code>**bold**</code> ·{" "}
                <code>[text](https://url)</code>. An unsubscribe footer is
                added automatically.
              </p>
            </div>

            {result && (
              <div
                className={cn(
                  "rounded-md border px-3 py-2 text-sm",
                  result.ok
                    ? "bg-success/5 border-success/30 text-success"
                    : "bg-danger/5 border-danger/30 text-danger",
                )}
              >
                {result.msg}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-56 px-3 py-2 text-sm rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
              />
              <button
                type="button"
                onClick={() => doSend("test")}
                disabled={!canSend || sending !== null}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
              >
                {sending === "test" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send test
              </button>

              <div className="flex-1" />

              <button
                type="button"
                onClick={() => doSend("all")}
                disabled={!canSend || sending !== null || active === 0}
                className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent/90 disabled:opacity-40"
              >
                {sending === "all" ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Send to {active} subscriber{active === 1 ? "" : "s"}
              </button>
            </div>
          </div>

          {/* Right — live preview (exact branded email) */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-xs font-medium text-slate-500">
                Preview — exactly how recipients see it
              </span>
              {previewing && (
                <Loader2 className="h-3 w-3 animate-spin text-slate-300" />
              )}
            </div>
            <div className="flex-1 min-h-[460px] rounded-md border border-slate-200 overflow-hidden bg-slate-100">
              {previewHtml ? (
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-full min-h-[460px] border-0 bg-white"
                />
              ) : (
                <div className="h-full min-h-[460px] flex items-center justify-center px-6 text-center text-xs text-slate-400">
                  Start typing a subject or body — the live email preview
                  appears here.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Subscribers ─────────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <span className="text-sm font-medium text-slate-900">
            Subscribers
          </span>
          <button
            type="button"
            onClick={copyEmails}
            disabled={activeEmails.length === 0}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-900 disabled:opacity-40"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-success" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
            {copied ? "Copied" : "Copy active emails"}
          </button>
        </div>

        {loadErr && (
          <div className="px-5 py-3 text-sm text-danger">{loadErr}</div>
        )}
        {!data && !loadErr && (
          <div className="p-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {data && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Email</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Source</th>
                  <th className="text-left px-4 py-2.5 font-semibold">
                    Subscribed
                  </th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.subscribers.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-5 py-8 text-center text-slate-500"
                    >
                      No subscribers yet.
                    </td>
                  </tr>
                )}
                {data.subscribers.map((s) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-5 py-2.5 font-medium text-slate-900">
                      <a
                        href={`mailto:${s.email}`}
                        className="hover:text-accent"
                      >
                        {s.email}
                      </a>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {s.source || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {fmtRelative(s.subscribed_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium",
                          s.is_active
                            ? "bg-success/10 text-success border-success/30"
                            : "bg-slate-100 text-slate-500 border-slate-200",
                        )}
                      >
                        {s.is_active ? "active" : "unsubscribed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
